import { Link } from 'react-router-dom'
import { Correct } from './Correct'
import type { ThreatView } from '../types/threats'

// Every rival Scout has seen at one of our accounts, in one panel. Presentational only: the verdict,
// the severity and the evidence link all come from the API. Exported so the account plan, the contact
// page and the cockpit can each adopt it without re-inventing the words or the colour.
//
// The rule the panel exists to enforce: a competitor at our client is a THREAT. It is never described
// as an opportunity, it never carries an amount, and it is read before the good news.

export interface ThreatsPanelProps {
  threats: ThreatView[]
  /** Cap the list; the rest are summarised on the last line. */
  max?: number
  /** Shown when there are none. */
  emptyNote?: string
  title?: string
  /** Hide the heading when the host screen already has one. */
  bare?: boolean
}

const DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const day = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime()) ? iso : DAY.format(d)
}

export function ThreatsPanel({ threats, max = 8, emptyNote, title = 'Threats', bare = false }: ThreatsPanelProps) {
  const shown = threats.slice(0, max)
  return (
    <section className={`threats-panel${bare ? ' bare' : ''}`} aria-label={title}>
      {!bare && (
        <header className="threats-head">
          <h2>{title}</h2>
          {threats.length > 0 && <span className="threat-count">{threats.length}</span>}
        </header>
      )}
      {threats.length === 0 ? (
        <p className="muted small">{emptyNote ?? 'No rival has been seen at any of your accounts.'}</p>
      ) : (
        <ul className="plain threat-list">
          {shown.map((t) => <ThreatRow key={t.threatId} threat={t} />)}
          {threats.length > shown.length && <li className="muted small">And {threats.length - shown.length} more.</li>}
        </ul>
      )}
    </section>
  )
}

export function ThreatRow({ threat: t }: { threat: ThreatView }) {
  const evidence = t.meetingId
    ? { to: `/meetings?view=agenda&date=${encodeURIComponent(t.occurredOn)}`, label: 'the meeting' }
    : t.engagementId
      ? { to: `/deals/${encodeURIComponent(t.engagementId)}`, label: 'the thread' }
      : null
  return (
    <li className={`threat-row sev-${t.severity}`}>
      <span className="threat-mark" aria-hidden="true">!</span>
      <span className="threat-body">
        <span className="threat-line">
          <strong>{t.headline}</strong>
          {t.severity === 'watch' && <span className="tag sig watch">to confirm</span>}
          <Correct kind="threat" entityId={t.threatId} entityLabel={t.headline} currentValue={t.severity === 'watch' ? 'watch' : 'rival'} compact />
        </span>
        <span className="threat-meta">
          {t.accountName && <><Link to={t.companyId ? `/accounts/${encodeURIComponent(t.companyId)}` : '/accounts'}>{t.accountName}</Link> · </>}
          <span>{day(t.occurredOn)}</span>
          <span> · seen in {t.seenIn === 'meeting' ? 'a meeting' : 'a thread'}</span>
          {evidence && <> · <Link to={evidence.to}>{evidence.label}</Link></>}
        </span>
        {t.detail && <span className="threat-detail muted small">{t.detail}</span>}
        {t.people.length > 0 && (
          <span className="threat-people muted small">In the room: {t.people.slice(0, 6).join(', ')}{t.people.length > 6 ? ` and ${t.people.length - 6} more` : ''}</span>
        )}
      </span>
    </li>
  )
}
